import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { AiScoreCell } from 'src/components/document-review/queue/AiScoreCell'
import { WaitingCell } from 'src/components/document-review/queue/WaitingCell'
import type { DocumentReviewQueueRow } from 'src/interfaces/documentReview'
import { parseReviewDate, resolveStepTypeLabel } from 'src/utils/documentReviewList'
import { contractorQueueHeaderTitles } from './constants'
import { ContractorQueueFields } from './enums'

/**
 * Wiring only — every non-trivial value comes from utils/documentReviewList.
 *
 * No Actions column: the whole row is clickable (see ContractorQueueTable's onRowClick), so a
 * Review button would be a second control for the same navigation.
 */
export const contractorQueueColumns: Array<GridColDef<DocumentReviewQueueRow>> = [
  {
    field: ContractorQueueFields.driverName,
    headerName: contractorQueueHeaderTitles[ContractorQueueFields.driverName],
    flex: 3,
    minWidth: 180,
    sortable: true,
    // Identifies the row, so it is never offered for hiding in the manage-columns panel.
    hideable: false,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<DocumentReviewQueueRow>) => {
      return (
        <Typography variant="body2" css={tw`truncate`}>
          {params.row.driverName ?? ''}
        </Typography>
      )
    },
  },
  {
    field: ContractorQueueFields.partnerName,
    headerName: contractorQueueHeaderTitles[ContractorQueueFields.partnerName],
    flex: 2,
    minWidth: 160,
    sortable: true,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<DocumentReviewQueueRow>) => {
      return (
        <Typography variant="body2" css={tw`truncate`}>
          {params.row.partnerName ?? ''}
        </Typography>
      )
    },
  },
  {
    field: ContractorQueueFields.type,
    headerName: contractorQueueHeaderTitles[ContractorQueueFields.type],
    flex: 2,
    minWidth: 160,
    sortable: true,
    display: 'flex',
    // The queue row carries no step metadata, so the insurance-upload override cannot apply
    // here — it needs `metadata.disableInsuranceUpload`, which only the detail call returns.
    // The review screen shows the overridden label.
    valueGetter: (value, row) => {
      return resolveStepTypeLabel({ type: row.type, disableInsuranceUpload: false })
    },
  },
  {
    field: ContractorQueueFields.finishedAt,
    headerName: contractorQueueHeaderTitles[ContractorQueueFields.finishedAt],
    flex: 1,
    minWidth: 120,
    sortable: true,
    display: 'flex',
    // Sorts on the real instant while the cell renders "3 days" — sorting the formatted string
    // would order "1 day" after "10 days".
    valueGetter: (value, row) => {
      return parseReviewDate(row.finishedAt)?.toMillis() ?? 0
    },
    renderCell: (params: GridRenderCellParams<DocumentReviewQueueRow>) => {
      return <WaitingCell finishedAt={params.row.finishedAt} />
    },
  },
  {
    field: ContractorQueueFields.slpRate,
    headerName: contractorQueueHeaderTitles[ContractorQueueFields.slpRate],
    flex: 1,
    minWidth: 110,
    sortable: true,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<DocumentReviewQueueRow>) => {
      return <AiScoreCell slpRate={params.row.slpRate} />
    },
  },
]
